import { useState, useEffect, useCallback } from 'react';
import { rasService } from '../services/rasService';
import { ProcessedStatsData } from '../types/api';

interface UseProcessedStatsResult {
  stats: ProcessedStatsData | null;
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
  refetch: () => void;
}

export const useProcessedStats = (hours: number = 24): UseProcessedStatsResult => {
  const [stats, setStats] = useState<ProcessedStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rasService.getProcessedStats(hours);
      
      if (response.success && response.data && response.data.length > 0) {
        setStats(response.data[0]);
        setLastRefresh(new Date());
      } else {
        setError('No processed stats data available');
        setStats(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch processed stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    fetchStats();

    // Auto-refresh every 60 minutes (3,600,000ms)
    const interval = setInterval(fetchStats, 3600000);
    
    return () => clearInterval(interval);
  }, [fetchStats]);

  return { stats, loading, error, lastRefresh, refetch: fetchStats };
};